import { useMemo } from 'react'
import { useCommandState } from 'cmdk'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import type { Ctx } from '@milkdown/ctx'

import { Button } from '@/components/ui/button'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@/components/ui/command'
import { Skeleton } from '@/components/ui/skeleton'
import { useSnippetsQuery } from '@/features/snippets/hooks/useSnippets'
import type { Snippet } from '@/features/snippets/types'
import { contentPreview, partitionSnippets } from '@/features/snippets/utils'
import { SnippetPickerItem } from './SnippetPickerItem'
import { insertSnippetReference } from '@/components/milkdown/snippet/insertSnippet'

/**
 * Shown when the org has no snippets at all. Keep this in step with the empty
 * state on the Snippets page — the picker cannot create one, so it points there.
 */
export const SNIPPETS_EMPTY_MESSAGE =
  'No snippets yet. Create one on the Snippets page.'

const SKELETON_ROW_COUNT = 3
/** Enough for the visible row; searching past it would rank on unseen text. */
const PREVIEW_MAX_CHARS = 200

/** Own component: useNavigate needs a Router, which surfaces without one never
    hit because this only mounts inside the open dialog's empty state. */
function EmptyStateCta({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  return (
    <div className="flex flex-col items-center gap-2">
      {SNIPPETS_EMPTY_MESSAGE}
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          onClose()
          navigate('/snippets')
        }}>
        Go to Snippets
      </Button>
    </div>
  )
}

/**
 * cmdk owns the filtering, so the visible count only exists inside its store.
 * Announced from a region that is mounted for the whole open dialog, so an
 * assistive tech has it registered before the count changes.
 */
function ResultCount({ ready }: { ready: boolean }) {
  const count = useCommandState((state) => state.filtered.count)
  return (
    <div role="status" aria-live="polite" className="sr-only">
      {/* Empty until the list lands: the region has to be mounted before its
          first real value to be announced, but "0 snippets" while still loading
          would be wrong. */}
      {ready ? (count === 1 ? '1 snippet' : `${count} snippets`) : ''}
    </div>
  )
}

/**
 * Rendered next to `<Milkdown />`. `target` is the editor ctx the toolbar button
 * or the `/snippet` slash command captured; null means closed. The library is
 * only fetched while open, so a duty page nobody inserts from costs no request.
 */
export function SnippetPicker({
  target,
  dutyId,
  onClose,
  onInsert,
}: {
  target: Ctx | null
  /** The duty the editor is scoped to, so this query shares the blocks' cache entry. */
  dutyId?: string
  onClose: () => void
  /** Arms the editor's edit gate — the dialog is a portal outside the editor root. */
  onInsert?: () => void
}) {
  const { data, isPending, isError, refetch } = useSnippetsQuery(
    target !== null,
    dutyId
  )
  // cmdk re-scores every row on every keystroke, so the preview is built once
  // per snippet rather than twice per row per keypress. Keyed on data?.snippets
  // because `?? []` would hand useMemo a new array on every render.
  const { careforceRows, ownRows } = useMemo(() => {
    const preview = (snippet: Snippet) => ({
      snippet,
      preview: contentPreview(snippet.content).slice(0, PREVIEW_MAX_CHARS),
    })
    const { careforce, own } = partitionSnippets(data?.snippets ?? [])
    return { careforceRows: careforce.map(preview), ownRows: own.map(preview) }
  }, [data?.snippets])
  const rowCount = careforceRows.length + ownRows.length

  const insert = (snippet: Snippet) => {
    if (!target) return
    // Always the SHARED id: the org's copy of a Careforce snippet answers to
    // it, so Reset never breaks a script, and the expander reads the copy
    // first anyway.
    // Refused for anything that is not a top-level block: a closed block slice
    // in a table cell splits the table, and the same applies to any nesting.
    if (!insertSnippetReference(target, snippet.reference_id)) {
      toast.error(
        'A snippet must sit at the top level of the script. Move the cursor outside any list, table or code block and try again.'
      )
      onClose()
      return
    }
    onInsert?.()
    onClose()
  }

  return (
    <CommandDialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Insert snippet"
      description="Inserts a linked snippet block at the cursor.">
      <CommandInput placeholder="Search snippets…" />
      <CommandList aria-label="Snippets" aria-busy={isPending}>
        {isPending && (
          <div className="space-y-2 p-2">
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-2 py-6 text-sm text-muted-foreground">
            Snippets could not be loaded.
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        )}

        {!isPending && !isError && (
          <>
            {/* cmdk renders this whenever the filtered count is 0, which covers
                both "no snippets at all" and "nothing matches the search". */}
            <CommandEmpty>
              {rowCount ? (
                'No snippets match your search.'
              ) : (
                <EmptyStateCta onClose={onClose} />
              )}
            </CommandEmpty>
            {/* A group with no rows, or no match, is not rendered: cmdk hides
                an empty CommandGroup. Both groups keep one search. */}
            {careforceRows.length > 0 && (
              <CommandGroup heading="Created by Careforce">
                {careforceRows.map((row) => (
                  <SnippetPickerItem
                    key={row.snippet.id}
                    {...row}
                    onSelect={insert}
                  />
                ))}
              </CommandGroup>
            )}
            {ownRows.length > 0 && (
              <CommandGroup
                heading={
                  careforceRows.length > 0 ? 'Your snippets' : undefined
                }>
                {ownRows.map((row) => (
                  <SnippetPickerItem
                    key={row.snippet.id}
                    {...row}
                    onSelect={insert}
                  />
                ))}
              </CommandGroup>
            )}
          </>
        )}
      </CommandList>
      {!isPending && !isError && careforceRows.length > 0 && (
        <p className="border-t border-neutral-subtle-border px-3 py-2 text-xs text-muted-foreground">
          Careforce snippets stay current automatically.
        </p>
      )}
      <ResultCount ready={!isPending && !isError} />
    </CommandDialog>
  )
}
